import {
  ArrowUpRight,
  Globe2,
  Info,
  MapPin,
  RotateCcw,
  Search,
  ShieldCheck,
} from "lucide-react";
import { useState } from "react";
import { ImportProgress } from "../../ImportProgress";
import type { ReportingDateFilter } from "../../communityReportingApi";
import { ReportingDateRange } from "./ReportingDateRange";
import { useReportingPeriod, type PeriodLoader } from "./useReportingPeriod";
import { completeTrendView } from "./periodModel";
import { provinces } from "./geography";
import type { Observatory, ObservatoryView } from "./schema";
import { useObservatoryCopy } from "./copy";
import { countRange, matches, reportingSearchTerms } from "./model";
import { ReportingMap } from "./ReportingMap";
import {
  RecurringPatterns,
  ReportingMatrix,
  ReportingSummary,
} from "./ReportingPanels";
import "../../styles/observatory.css";
export function ObservatoryDashboard({
  data: baseData,
  showMethods = true,
  dateFilter,
  loadPeriod,
}: {
  data?: Observatory | null | undefined;
  showMethods?: boolean;
  dateFilter?: ReportingDateFilter | null | undefined;
  loadPeriod?: PeriodLoader | undefined;
}) {
  const { locale, t } = useObservatoryCopy();
  const period = useReportingPeriod(baseData, loadPeriod);
  const data = period.data;
  const [location, setLocation] = useState("all"),
    [community, setCommunity] = useState(""),
    [platform, setPlatform] = useState(""),
    [threat, setThreat] = useState(""),
    [query, setQuery] = useState(""),
    [tab, setTab] = useState("overview");
  const view = data?.views.find(
    (v) =>
      v.location === location &&
      v.community === community &&
      v.platform === platform &&
      v.threat === threat,
  );
  const values = (field: "community" | "platform" | "threat") =>
    [
      ...new Set(baseData?.views.map((v) => v[field]).filter(Boolean) ?? []),
    ].sort();
  const locationName = (value: string) =>
    value === "all"
      ? t("global")
      : value === "outside"
        ? t("outside")
        : value.startsWith("country:")
          ? (baseData?.countries[value.slice(8)] ?? t("unknown"))
          : locale === "fa"
            ? (Object.values(provinces).find((p) => p.name === value.slice(9))
                ?.fa ?? value.slice(9))
            : value.slice(9);
  const selectView = (v: ObservatoryView) => {
    setLocation(v.location);
    setCommunity(v.community);
    setPlatform(v.platform);
    setThreat(v.threat);
    setQuery("");
    setTab("overview");
  };
  const results = query.trim()
    ? (data?.views
        .filter((v) => v.location !== "outside")
        .filter((v) =>
          matches(
            query,
            [
              locationName(v.location),
              v.location,
              v.community,
              v.platform,
              v.threat,
              ...(v.dimensions.months ?? []).map((m) => m.label),
            ].flatMap((value) => reportingSearchTerms(value, locale)),
          ),
        )
        .slice(0, 12) ?? [])
    : [];
  function reset() {
    period.clear();
    setLocation("all");
    setCommunity("");
    setPlatform("");
    setThreat("");
    setQuery("");
    setTab("overview");
  }
  if (!baseData)
    return (
      <section
        className="observatory observatory-publication-state"
        aria-labelledby="observatory-publication-heading"
      >
        <span className="observatory-publication-icon">
          <MapPin size={28} aria-hidden="true" />
        </span>
        <div>
          <span className="observatory-badge">{t("categoryPublication")}</span>
          <h2 id="observatory-publication-heading">
            {t("awaitingPublication")}
          </h2>
          <p>{t("awaitingPublicationHelp")}</p>
        </div>
      </section>
    );
  return (
    <div className="observatory">
      <div className="observatory-searchbar">
        <label className="observatory-search-label">
          {t("search")}
          <span>
            <Search size={18} aria-hidden="true" />
            <input
              type="search"
              aria-label={t("search")}
              value={query}
              placeholder={t("searchHint")}
              onChange={(e) => setQuery(e.target.value)}
            />
          </span>
        </label>
        <label>
          {t("community")}
          <select
            aria-label={t("community")}
            value={community}
            onChange={(e) => setCommunity(e.target.value)}
          >
            <option value="">{t("any")}</option>
            {values("community").map((v) => (
              <option key={v}>{v}</option>
            ))}
          </select>
        </label>
        <label>
          {t("threat")}
          <select
            value={threat}
            aria-label={t("threat")}
            onChange={(e) => setThreat(e.target.value)}
          >
            <option value="">{t("any")}</option>
            {values("threat").map((v) => (
              <option key={v}>{v}</option>
            ))}
          </select>
        </label>
        <label>
          {t("platform")}
          <select
            aria-label={t("platform")}
            value={platform}
            onChange={(e) => setPlatform(e.target.value)}
          >
            <option value="">{t("any")}</option>
            {values("platform").map((v) => (
              <option key={v}>{v}</option>
            ))}
          </select>
        </label>
        <label>
          {t("location")}
          <select
            aria-label={t("location")}
            value={location}
            onChange={(e) => setLocation(e.target.value)}
          >
            <option value="all">{t("worldScope")}</option>
            {Object.entries(baseData.countries).map(([code, name]) => (
              <option key={code} value={`country:${code}`}>
                {name}
              </option>
            ))}
            {Object.entries(baseData.provinces).map(([name, fa]) => (
              <option key={name} value={`province:${name}`}>
                {locale === "fa" ? fa : name}
              </option>
            ))}
          </select>
        </label>
        <button
          className="observatory-reset"
          onClick={reset}
          aria-label={t("reset")}
          title={t("reset")}
        >
          <RotateCcw size={17} aria-hidden="true" />
        </button>
      </div>
      {query.trim() && period.phase === "idle" && (
        <section className="observatory-search-results" aria-live="polite">
          {results.length ? (
            results.map((v, i) => (
              <button key={i} onClick={() => selectView(v)}>
                {[locationName(v.location), v.community, v.threat, v.platform]
                  .filter(Boolean)
                  .join(" · ")}{" "}
                <bdi>{countRange(v.cases)}</bdi>
              </button>
            ))
          ) : (
            <p>{t("noSearch")}</p>
          )}
        </section>
      )}
      <ReportingDateRange
        bounds={loadPeriod ? dateFilter : undefined}
        applied={period.applied}
        loading={period.phase === "loading"}
        resetKey={period.resetKey}
        onApply={(from, to) => void period.apply(from, to)}
        onClear={period.clear}
      />
      <div className="observatory-scope">
        <strong>{t("scope")}</strong>
        <button
          aria-pressed={
            location !== "country:364" &&
            !location.startsWith("province:") &&
            tab !== "crossBorder"
          }
          onClick={() => {
            setLocation("all");
            setTab("overview");
          }}
        >
          {t("worldScope")}
        </button>
        <button
          aria-pressed={
            (location === "country:364" || location.startsWith("province:")) &&
            tab !== "crossBorder"
          }
          onClick={() => {
            setLocation("country:364");
            setTab("overview");
          }}
        >
          {t("iranScope")}
        </button>
        <span className="observatory-window">
          <Globe2 size={14} aria-hidden="true" />
          {location === "all" ? t("worldContext") : locationName(location)}
        </span>
      </div>
      {period.phase === "loading" ? (
        <ImportProgress label={t("periodLoading")} />
      ) : period.phase === "error" ? (
        <section
          className="observatory-panel"
          role="alert"
          data-kavoosh-inline-validation="true"
        >
          <p>{t("periodError")}</p>
          <button
            onClick={() => {
              if (period.applied)
                void period.apply(period.applied.from, period.applied.to);
            }}
          >
            {t("retryPeriod")}
          </button>
          <button onClick={period.clear}>{t("allDates")}</button>
        </section>
      ) : tab === "crossBorder" ? (
        <section className="observatory-panel observatory-review-pane">
          <ShieldCheck size={30} aria-hidden="true" />
          <h2>{t("crossBorder")}</h2>
          <h3>{t("outside")}</h3>
          <p>{t("outsideHelp")}</p>
          <h3>{t("reviewed")}</h3>
          <p role="status">{t("reviewHelp")}</p>
          <button onClick={() => setTab("overview")}>{t("overview")}</button>
        </section>
      ) : (
        <div className="observatory-grid">
          <ReportingMap
            data={data}
            location={location}
            onSelect={setLocation}
            filters={{ community, platform, threat }}
          />
          {view ? (
            <>
              <ReportingSummary
                view={view}
                title={locationName(location)}
                global={location === "all"}
                onThreat={setThreat}
              />
              <ReportingMatrix
                view={view}
                query=""
                onSelect={(g, a) => {
                  setCommunity(g);
                  setThreat(a);
                }}
              />
              <RecurringPatterns
                view={completeTrendView(view, period.applied)}
                onSelect={setThreat}
              />
            </>
          ) : (
            <section className="observatory-panel observatory-selection-unavailable">
              <Info size={28} aria-hidden="true" />
              <h2>{locationName(location)}</h2>
              <p role="status">{t("insufficient")}</p>
            </section>
          )}
        </div>
      )}
      <aside className="observatory-cross-border-strip">
        <Globe2 size={27} aria-hidden="true" />
        <h2>{t("crossBorder")}</h2>
        <p>{t("crossBorderSummary")}</p>
        <button
          className="observatory-text-button"
          onClick={() => setTab("crossBorder")}
        >
          {t("reviewCriteria")}
          <ArrowUpRight size={15} aria-hidden="true" />
        </button>
      </aside>
      <p className="observatory-footnote observatory-bottom-note">
        <Info size={14} aria-hidden="true" />
        {t("caseHelp")}
      </p>
      {showMethods && (
        <details className="observatory-methods">
          <summary>{t("method")}</summary>
          <p>{t("privacy")}</p>
          <p>{t("matrixHelp")}</p>
          <p>{t("publicSearch")}</p>
          <p>{t("mapLegend")}</p>
          <p>{t("dateHelp")}</p>
          <p>{t("recurrenceHelp")}</p>
          <p>{t("outsideHelp")}</p>
          <p>{t("reviewHelp")}</p>
          <h3>{t("sources")}</h3>
          <ul>
            <li>
              <a
                href="https://service-manual.ons.gov.uk/data-visualisation/guidance/dashboards"
                target="_blank"
                rel="noreferrer"
              >
                ONS · Dashboard design
              </a>
            </li>
            <li>
              <a
                href="https://freedomhouse.org/report/transnational-repression/about-acknowledgements"
                target="_blank"
                rel="noreferrer"
              >
                Freedom House · Transnational repression methodology
              </a>
            </li>
            <li>
              <a
                href="https://citizenlab.ca/research/psychological-emotional-war-digital-transnational-repression-canada/"
                target="_blank"
                rel="noreferrer"
              >
                Citizen Lab · Digital transnational repression
              </a>
            </li>
          </ul>
        </details>
      )}
    </div>
  );
}
